import React, { useEffect, useMemo, useState } from "react";

import api, { handleApi } from "pages/api/api";
import { useAlert } from "pages/context/AlertContext";
import { useAuth } from "pages/context/AuthContext";
import ProjectTable from "pages/components/ProjectTable/ProjectTable";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import CircularProgress from "@mui/material/CircularProgress";
import Paper from "@mui/material/Paper";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";

import Draggable from "react-draggable";

function PaperComponent(props) {
  return (
    <Draggable
      handle=".draggable-vendedor-faturas-title"
      cancel={'[class*="MuiDialogContent-root"]'}
    >
      <Paper {...props} />
    </Draggable>
  );
}

function fmtData(value) {
  if (!value) return "";

  const s = String(value).slice(0, 10);
  const partes = s.split("-");

  if (partes.length !== 3) return s;

  return `${partes[2]}/${partes[1]}/${partes[0]}`;
}

function fmtNumero(value, casas = 2) {
  const n = Number(value);

  if (!Number.isFinite(n)) return "";

  return n.toLocaleString("pt-PT", {
    minimumFractionDigits: casas,
    maximumFractionDigits: casas,
  });
}

export default function DialogObjetivoV2DashboardVendedorFaturas({
  open,
  onClose,
  idObjetivo,
  vendedor,
}) {
  const { showAlert } = useAlert();
  const { userData } = useAuth();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [loading, setLoading] = useState(false);
  const [detalhe, setDetalhe] = useState(null);

  useEffect(() => {
    if (!open || !idObjetivo || !vendedor?.referencia) {
      setDetalhe(null);
      return;
    }

    let ativo = true;

    const carregar = async () => {
      setLoading(true);

      try {
        const [data, err] = await handleApi(
          api.post("Comercial/SelObjetivoV2DashboardVendedorFaturas", {
            idobjetivo: idObjetivo,
            refvendedor: vendedor.referencia,
            idutilizador: userData?.id || 0,
          })
        );

        if (!ativo) return;

        if (err) {
          setDetalhe(null);
          showAlert(err, "error");
          return;
        }

        setDetalhe(data || null);
      } finally {
        if (ativo) {
          setLoading(false);
        }
      }
    };

    carregar();

    return () => {
      ativo = false;
    };
  }, [open, idObjetivo, vendedor?.referencia, userData?.id, showAlert]);

  const data = useMemo(
    () => (Array.isArray(detalhe?.faturas) ? detalhe.faturas : []),
    [detalhe?.faturas]
  );

  const columns = useMemo(
    () => [
      {
        Header: "QTD",
        accessor: "qtd",
        width: 100,
        minWidth: 90,
        align: "right",
        headerAlign: "right",
        Cell: ({ value }) => fmtNumero(value, 0),
      },
      {
        Header: "Euros",
        accessor: "euros",
        width: 110,
        minWidth: 100,
        align: "right",
        headerAlign: "right",
        Cell: ({ value }) => fmtNumero(value, 2),
      },
      {
        Header: "Litros",
        accessor: "litros",
        width: 110,
        minWidth: 100,
        align: "right",
        headerAlign: "right",
        Cell: ({ value }) => fmtNumero(value, 3),
      },
      {
        Header: "Cliente",
        accessor: "cliente",
        width: 250,
        minWidth: 220,
      },
      {
        Header: "Submarca",
        accessor: "submarca",
        width: 180,
        minWidth: 150,
      },
      {
        Header: "Data",
        accessor: "faturadata",
        width: 110,
        minWidth: 105,
        Cell: ({ value }) => fmtData(value),
      },
      {
        Header: "Fatura",
        accessor: "faturanumero",
        width: 130,
        minWidth: 120,
      },
      {
        Header: "Material",
        accessor: "material",
        width: 360,
        minWidth: 300,
      },
    ],
    []
  );

  const nomeVendedor = detalhe?.vendedor?.nome || vendedor?.nome || vendedor?.referencia || "";

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xl"
      fullScreen={isMobile}
      PaperComponent={isMobile ? undefined : PaperComponent}
      PaperProps={{
        sx: {
          width: {
            xs: "100%",
            sm: "calc(100% - 32px)",
          },
          maxWidth: {
            xs: "100%",
            sm: "96vw",
          },
          maxHeight: {
            xs: "100%",
            sm: "92vh",
          },
        },
      }}
    >
      <DialogTitle
        className="draggable-vendedor-faturas-title"
        sx={{
          cursor: {
            xs: "default",
            sm: "move",
          },
        }}
      >
        <MDTypography variant="h5">
          Detalhe de vendas
          {nomeVendedor ? ` — ${nomeVendedor}` : ""}
        </MDTypography>

        {detalhe?.objetivo && (
          <MDTypography variant="button" color="text" display="block" mt={0.5}>
            {fmtData(detalhe.objetivo.dataini)}
            {" a "}
            {fmtData(detalhe.objetivo.datafim)}
          </MDTypography>
        )}
      </DialogTitle>

      <DialogContent
        dividers
        sx={{
          px: {
            xs: 1,
            sm: 2,
          },
          py: {
            xs: 1,
            sm: 2,
          },
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
        }}
      >
        {loading ? (
          <MDBox py={4} display="flex" justifyContent="center">
            <CircularProgress />
          </MDBox>
        ) : (
          <>
            {detalhe?.totais && (
              <MDBox display="flex" flexWrap="wrap" gap={2} mb={2}>
                <MDTypography variant="button" color="text">
                  Linhas: <strong>{detalhe?.total_linhas ?? 0}</strong>
                </MDTypography>

                <MDTypography variant="button" color="text">
                  QTD: <strong>{fmtNumero(detalhe.totais.qtd, 0)}</strong>
                </MDTypography>

                <MDTypography variant="button" color="text">
                  Euros: <strong>{fmtNumero(detalhe.totais.euros, 2)}</strong>
                </MDTypography>

                <MDTypography variant="button" color="text">
                  Litros: <strong>{fmtNumero(detalhe.totais.litros, 3)}</strong>
                </MDTypography>
              </MDBox>
            )}

            <MDBox
              sx={{
                flex: 1,
                minHeight: 0,
                overflow: "hidden",
              }}
            >
              <ProjectTable
                tableKey={`objetivos-v2-dashboard-vendedor-faturas-${vendedor?.referencia || "x"}`}
                version="v1"
                columns={columns}
                data={data}
                loading={loading}
                emptyMessage="Sem vendas para apresentar."
                minWidth={1450}
                maxHeight="62vh"
                showKeepFilters
                defaultSortBy={[
                  {
                    id: "faturadata",
                    desc: false,
                  },
                ]}
              />
            </MDBox>
          </>
        )}
      </DialogContent>

      <DialogActions>
        <MDButton color="secondary" onClick={onClose}>
          Fechar
        </MDButton>
      </DialogActions>
    </Dialog>
  );
}
